///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { GtthSectorObstruction } from './sector/sector-obstruction.enum';
import { SectorConstants } from './sector/view/sector.constants';
import { isDefined } from '@siklu/sui-lib/utils';

export class AngleUtils {
    static normalize(angle: number): number {
        if (!isFinite(angle)) {
            throw Error('Could not normalize not finite value ' + angle);
        }

        while (angle < 0) {
            angle += 360;
        }

        return (360 + angle) % 360;
    }

    static isBetween(angle: number, min: number, max: number): boolean {
        const _min = AngleUtils.normalize(min);
        let _max = AngleUtils.normalize(max);
        let _angle = AngleUtils.normalize(angle);

        if (_min > _max) {
            // if min and max are between 0
            _max += 360;
            _angle = _angle < 180 ? _angle + 360 : _angle;
        }

        return _angle >= _min && _angle <= _max;
    }

    static calculateSectorMinMax(
        angles: number[],
        obstructionAngle?: number,
        obstructionDirection?: GtthSectorObstruction
    ): { min: number; max: number } {
        let minAngle = Math.min(...angles);
        let maxAngle = Math.max(...angles);

        if (maxAngle - minAngle > SectorConstants.SECTOR_WIDTH_ANGLE) {
            minAngle = Math.min(...angles.filter((angle) => angle > 180));
            maxAngle = Math.max(...angles.filter((angle) => angle < 180));
        }

        let leftHalfSector = SectorConstants.SECTOR_WIDTH_ANGLE / 2;
        let rightHalfSector = SectorConstants.SECTOR_WIDTH_ANGLE / 2;

        if (obstructionAngle) {
            if (obstructionDirection === GtthSectorObstruction.LEFT) {
                rightHalfSector -= obstructionAngle;
            } else if (obstructionDirection === GtthSectorObstruction.RIGHT) {
                leftHalfSector -= obstructionAngle;
            }
        }

        return {
            min: AngleUtils.normalize(Math.ceil(maxAngle - leftHalfSector)),
            max: AngleUtils.normalize(Math.floor(minAngle + rightHalfSector))
        };
    }

    static calculateAllSectorsBounds(
        sectorBounds: { min: number; max: number }[],
        sectorsOffset: number[]
    ): { min: number; max: number }[] {
        let absoluteMin;
        let absoluteMax;

        sectorBounds.forEach((minMax: { min: number; max: number }, index: number) => {
            if (minMax.min === undefined && minMax.max === undefined) {
                return;
            }

            const correctedMin = AngleUtils.normalize(minMax.min - sectorsOffset[index]);
            const correctedMax = AngleUtils.normalize(minMax.max - sectorsOffset[index]);

            if (!isDefined(absoluteMax) && !isDefined(absoluteMin)) {
                absoluteMin = correctedMin;
                absoluteMax = correctedMax;
                return;
            }

            if (Math.abs(absoluteMin - correctedMin) > SectorConstants.SECTOR_WIDTH_ANGLE) {
                absoluteMin = Math.min(absoluteMin, correctedMin);
            } else {
                absoluteMin = Math.max(absoluteMin, correctedMin);
            }

            if (Math.abs(absoluteMax - correctedMax) > SectorConstants.SECTOR_WIDTH_ANGLE) {
                absoluteMax = Math.max(absoluteMax, correctedMax);
            } else {
                absoluteMax = Math.min(absoluteMax, correctedMax);
            }
        });

        sectorBounds.forEach((minMax: { min: number; max: number }, index: number) => {
            minMax.min = AngleUtils.normalize(absoluteMin + sectorsOffset[index]);
            minMax.max = AngleUtils.normalize(absoluteMax + sectorsOffset[index]);
        });

        return sectorBounds;
    }
}
